import React from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'gatsby-plugin-react-i18next';
import Colors from '@components/shared/Colors';
import { TAMPON_COMPARISON_COATED, TAMPON_COMPARISON_NAKED } from '@constants';
import Drops3 from '@images/account/tamponselector-icon-super.svg';
import Drops2 from '@images/account/tamponselector-icon-regular.svg';
import { CountryCode } from '@dayetopia/types';
import { useLocation } from '@reach/router';
import useReplacedInnerHtml from '@utils/useReplacedInnerHtml';

interface Props {
  country: CountryCode;
}

const CompareTamponsBody: React.FC<Props> = ({ country }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const organicTamponsLandingURL = '/products/organic-tampons';
  const hideCBDWords = country === 'US' || !location || location?.pathname?.includes(organicTamponsLandingURL);
  const CBDText = hideCBDWords ? 'Coated' : 'CBD';
  const formulaTextRef = useReplacedInnerHtml(t('components.compare-tampons-modal.featuring-formula'));

  return (
    <ModalBody>
      <h3>{t('components.compare-tampons-modal.tampon-sizes')}</h3>
      <GridWrap>
        <div style={{ border: `1px solid ${Colors.paleGold}` }}>
          <Drops2 />
          <h5>{t('components.compare-tampons-modal.regular')}</h5>
          <p>
            <Trans i18nKey="components.compare-tampons-modal.regular-absorbs" components={{ br: <br /> }} />
          </p>
        </div>
        <div style={{ border: `1px solid ${Colors.paleGold}` }}>
          <Drops3 />
          <h5>{t('components.compare-tampons-modal.super')}</h5>
          <p>
            <Trans i18nKey="components.compare-tampons-modal.super-absorbs" components={{ br: <br /> }} />
          </p>
        </div>
      </GridWrap>
      <h3>{t('components.compare-tampons-modal.tampon-kinds')}</h3>
      <GridWrap>
        <div>
          <img src={TAMPON_COMPARISON_NAKED} />
          <div>
            <h5>{t('components.naked')}</h5>
          </div>
        </div>
        <div>
          <img src={TAMPON_COMPARISON_COATED} />
          <div>
            <h5>{CBDText}</h5>
            <p ref={formulaTextRef} />
          </div>
        </div>
      </GridWrap>
    </ModalBody>
  );
};

export default CompareTamponsBody;

const ModalBody = styled.div`
  padding: 40px 40px 30px;
  text-align: center;

  @media (max-width: 768px) {
    padding: 0 0 30px;
  }

  h3 {
    font-family: 'Domaine';
    font-style: normal;
    font-weight: normal;
    font-size: 24px;
    line-height: 140%;
    text-align: center;
    color: ${Colors.forestGreen};
    margin-bottom: 30px;
  }
`;

const GridWrap = styled.div`
  display: flex;
  flex-direction: row;
  gap: 30px;
  align-items: flex-start;
  justify-content: center;
  color: ${Colors.forestGreen};
  margin-bottom: 60px;

  @media (max-width: 768px) {
    margin-bottom: 40px;
  }

  & > div {
    width: 150px;
    min-height: 150px;
    border-radius: 3px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;

    @media (max-width: 400px) {
      width: 140px;
      min-height: 140px;
    }
  }

  svg {
    height: 18px;
  }

  h5 {
    font-family: 'BrownStd';
    font-style: normal;
    font-weight: 700;
    font-size: 15px;
    line-height: 140%;
    text-align: center;
    letter-spacing: 1.5px;
    text-transform: uppercase;
  }

  p {
    font-family: 'BrownStd';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 140%;
    text-align: center;
  }
`;
